import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { supportService } from '../../services/support/supportService';
import { isMessageDuplicate, supportData } from '../../pages/Support/utils/supportHelpers';

const SOCKET_SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';

const SupportContext = createContext();

export const useSupport = () => useContext(SupportContext);

export const SupportProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Active Tab & Route Params
  const currentTab = searchParams.get('tab') || 'travellers';
  const selectedChatBookingId = searchParams.get('booking');
  const selectedChatGuestName = searchParams.get('guest');
  const selectedTicketId = searchParams.get('id');
  const isNewTicketAction = searchParams.get('action') === 'new';

  const [agentData, setAgentData] = useState(null);

  // ==========================================
  // TRAVELLER CHATS STATE
  // ==========================================
  const [travellerChats, setTravellerChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [chatThread, setChatThread] = useState(null);
  const [activeVoucherInfo, setActiveVoucherInfo] = useState(null);
  const [travellerMessageText, setTravellerMessageText] = useState('');
  const [travellerChatAttachment, setTravellerChatAttachment] = useState(null);
  const [travellerChatAttachmentPreview, setTravellerChatAttachmentPreview] = useState(null);
  const [isSendingTravellerMsg, setIsSendingTravellerMsg] = useState(false);
  const [chatFilter, setChatFilter] = useState('all'); // 'all' | 'unread'

  // ==========================================
  // TRRIP HELPDESK TICKETS STATE
  // ==========================================
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [issue, setIssue] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [ticketReplies, setTicketReplies] = useState({});
  const [chatAttachment, setChatAttachment] = useState(null);
  const [chatAttachmentPreview, setChatAttachmentPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const chatEndRef = useRef(null);
  const travellerChatContainerRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const travellerFileInputRef = useRef(null);
  const socketRef = useRef(null);
  const processedMessageIds = useRef(new Set());

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTravellerBottom = () => {
    if (travellerChatContainerRef.current) {
      travellerChatContainerRef.current.scrollTop = travellerChatContainerRef.current.scrollHeight;
    }
  };

  // Fetch Agent Profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await supportService.getAgentProfile();
        setAgentData(data?.data || null);
      } catch (error) {
        console.error('Error fetching agent profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Synchronize processedMessageIds when chatThread is loaded
  useEffect(() => {
    if (chatThread?.messages) {
      processedMessageIds.current = new Set(
        chatThread.messages.map((m) => m._id || `${m.text}_${m.timestamp}`)
      );
    } else {
      processedMessageIds.current = new Set();
    }
  }, [chatThread]);

  // Socket Connection for Real-time Updates
  useEffect(() => {
    const rawAgentId = localStorage.getItem('agent_id');
    const rawUserId = localStorage.getItem('user_id');
    const agentId = rawAgentId || rawUserId;
    if (!agentId) return;

    socketRef.current = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      if (rawAgentId) socketRef.current.emit('join-agent-home', { agentId: rawAgentId });
      if (rawUserId && rawUserId !== rawAgentId) socketRef.current.emit('join-agent-home', { agentId: rawUserId });
      if (selectedChatBookingId) {
        socketRef.current.emit('join-chat', { bookingId: selectedChatBookingId });
        socketRef.current.emit('join-voucher', { voucherId: selectedChatBookingId });
      }
    });

    // Real-time traveller new message
    socketRef.current.on('traveller-new-message', (data) => {
      fetchTravellerChats();

      if (selectedChatBookingId && data.booking_id && selectedChatBookingId.toLowerCase() === data.booking_id.toLowerCase()) {
        if (data.guest_name && selectedChatGuestName && data.guest_name.toLowerCase() !== selectedChatGuestName.toLowerCase()) {
          return;
        }

        const msg = data.message;
        if (!msg) return;

        const msgId = msg._id ? String(msg._id) : null;
        const msgKey = msgId || `${msg.text}_${msg.timestamp}`;
        if (processedMessageIds.current.has(msgKey)) return;
        processedMessageIds.current.add(msgKey);
        if (msgId) processedMessageIds.current.add(msgId);

        setChatThread((prev) => {
          if (!prev) return prev;
          const existing = prev.messages || [];
          if (isMessageDuplicate(existing, msg)) return prev;
          return {
            ...prev,
            messages: [...existing, msg]
          };
        });
        setTimeout(scrollToTravellerBottom, 100);
      }
    });

    socketRef.current.on('traveller-chat-updated', (data) => {
      fetchTravellerChats();

      if (selectedChatBookingId && data.booking_id && selectedChatBookingId.toLowerCase() === data.booking_id.toLowerCase()) {
        if (data.guest_name && selectedChatGuestName && data.guest_name.toLowerCase() !== selectedChatGuestName.toLowerCase()) {
          return;
        }

        const msg = data.message;
        if (!msg) return;

        const msgId = msg._id ? String(msg._id) : null;
        const msgKey = msgId || `${msg.text}_${msg.timestamp}`;
        if (processedMessageIds.current.has(msgKey)) return;
        processedMessageIds.current.add(msgKey);
        if (msgId) processedMessageIds.current.add(msgId);

        setChatThread((prev) => {
          if (!prev) return prev;
          const existing = prev.messages || [];
          if (isMessageDuplicate(existing, msg)) return prev;
          return {
            ...prev,
            messages: [...existing, msg]
          };
        });
        setTimeout(scrollToTravellerBottom, 100);
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [selectedChatBookingId, selectedChatGuestName]);

  // Load chats or tickets depending on active tab
  useEffect(() => {
    if (currentTab === 'travellers') {
      fetchTravellerChats();
    } else {
      fetchTickets();
    }
  }, [currentTab]);

  // Load specific traveller chat thread when selected
  useEffect(() => {
    if (selectedChatBookingId && currentTab === 'travellers') {
      fetchChatThread(selectedChatBookingId, selectedChatGuestName);
    }
  }, [selectedChatBookingId, selectedChatGuestName, currentTab]);

  const fetchTravellerChats = async () => {
    try {
      setLoadingChats(true);
      const res = await supportService.getTravellerChats();
      if (res && res.success) {
        setTravellerChats(res.data?.chats || []);
      }
    } catch (err) {
      console.error('Error fetching traveller chats:', err);
    } finally {
      setLoadingChats(false);
    }
  };

  const fetchChatThread = async (bookingId, guestName) => {
    try {
      const res = await supportService.getChatThread(bookingId, guestName);
      if (res && res.success) {
        setChatThread(res.data?.chat);
        setActiveVoucherInfo(res.data?.voucher);
        setTimeout(scrollToTravellerBottom, 100);
      }
    } catch (err) {
      console.error('Error fetching chat thread:', err);
      toast.error('Failed to load chat conversation');
    }
  };

  const fetchTickets = async () => {
    try {
      setLoadingTickets(true);
      const data = await supportService.getTickets();
      setTickets(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSelectChat = (bookingId, guestName) => {
    if (!bookingId) return;
    const cleanId = bookingId.toLowerCase();
    const cleanGuest = guestName ? guestName.trim() : '';
    setSearchParams({ tab: 'travellers', booking: cleanId, guest: cleanGuest });
    fetchChatThread(cleanId, cleanGuest);
  };

  const handleBackToChatList = () => {
    setSearchParams({ tab: currentTab });
  };

  const handleSelectTicket = (ticketId) => {
    setSearchParams({ tab: 'helpdesk', id: ticketId });
  };

  const handleOpenNewTicket = () => {
    setSearchParams({ tab: 'helpdesk', action: 'new' });
  };

  const handleBackToTicketList = () => {
    setSearchParams({ tab: 'helpdesk' });
  };

  // Send message to Traveller
  const handleSendTravellerMessage = async () => {
    if (!selectedChatBookingId) return;
    if (!travellerMessageText.trim() && !travellerChatAttachment) return;

    try {
      setIsSendingTravellerMsg(true);
      const formData = new FormData();
      if (travellerMessageText.trim()) formData.append('text', travellerMessageText.trim());
      if (travellerChatAttachment) formData.append('attachment', travellerChatAttachment);
      if (selectedChatGuestName) formData.append('guestName', selectedChatGuestName);

      const res = await supportService.sendTravellerMessage(selectedChatBookingId, formData);

      if (res && res.success) {
        setTravellerMessageText('');
        setTravellerChatAttachment(null);
        setTravellerChatAttachmentPreview(null);

        // Optimistically update thread
        if (res.data?.sentMessage) {
          const msg = res.data.sentMessage;
          const msgId = msg._id ? String(msg._id) : null;
          const msgKey = msgId || `${msg.text}_${msg.timestamp}`;
          processedMessageIds.current.add(msgKey);
          if (msgId) processedMessageIds.current.add(msgId);

          setChatThread((prev) => {
            if (!prev) return prev;
            const existing = prev.messages || [];
            if (isMessageDuplicate(existing, msg)) return prev;
            return {
              ...prev,
              messages: [...existing, msg]
            };
          });
        }
        setTimeout(scrollToTravellerBottom, 100);
        fetchTravellerChats();
      }
    } catch (err) {
      console.error('Failed to send traveller message:', err);
      toast.error('Failed to send message');
    } finally {
      setIsSendingTravellerMsg(false);
    }
  };

  const handleTravellerAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTravellerChatAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => setTravellerChatAttachmentPreview(reader.result);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // ==========================================
  // HELPDESK TICKET HANDLERS
  // ==========================================
  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => setScreenshotPreview(reader.result);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleChatAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChatAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => setChatAttachmentPreview(reader.result);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (isSubmitting || !issue.trim()) {
      if (!issue.trim()) toast.error('Please describe your issue.');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('issue_description', issue);
      formData.append('agent_name', agentData?.name || '');
      formData.append('agent_email', agentData?.email || '');
      formData.append('agent_phone', agentData?.phone_number || '');
      if (screenshot) formData.append('screenshot', screenshot);

      const response = await supportService.createTicket(formData);

      toast.success('Ticket raised successfully!');
      setIssue('');
      setScreenshot(null);
      setScreenshotPreview(null);
      await fetchTickets();
      if (response?.ticket?._id) {
        setSearchParams({ tab: 'helpdesk', id: response.ticket._id });
      }
    } catch (error) {
      toast.error('Failed to raise ticket.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendTicketMessage = async (ticketId) => {
    const message = ticketReplies[ticketId];
    if ((!message || !message.trim()) && !chatAttachment) return;

    try {
      setIsSendingMessage(true);
      const formData = new FormData();
      formData.append('message', message || '');
      if (chatAttachment) formData.append('attachment', chatAttachment);

      await supportService.sendTicketMessage(ticketId, formData);

      setTicketReplies((prev) => ({ ...prev, [ticketId]: '' }));
      setChatAttachment(null);
      setChatAttachmentPreview(null);

      await fetchTickets();
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Computations & Filterings
  const totalUnreadTravellerCount = travellerChats.reduce(
    (acc, c) => acc + (c.unread_agent_count || 0),
    0
  );

  const getFilteredTravellerChats = (isChatUnlocked) => {
    const activeList = isChatUnlocked
      ? travellerChats
      : travellerChats.length > 0
      ? travellerChats
      : supportData.mockTravellerChats;

    return activeList.filter((c) => {
      const matchSearch =
        c.booking_id?.toLowerCase().includes(chatSearchTerm.toLowerCase()) ||
        (c.guest_name && c.guest_name.toLowerCase().includes(chatSearchTerm.toLowerCase())) ||
        (c.last_message && c.last_message.toLowerCase().includes(chatSearchTerm.toLowerCase()));

      if (chatFilter === 'unread') {
        return matchSearch && (c.unread_agent_count || 0) > 0;
      }
      return matchSearch;
    });
  };

  const filteredTickets = tickets.filter(
    (t) =>
      t.issue_description?.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
      t._id?.includes(ticketSearchTerm)
  );

  const selectedTicket = tickets.find((t) => t._id === selectedTicketId);

  const value = {
    // Navigation & Params
    currentTab,
    selectedChatBookingId,
    selectedChatGuestName,
    selectedTicketId,
    isNewTicketAction,
    setSearchParams,
    handleSelectChat,
    handleBackToChatList,
    handleSelectTicket,
    handleOpenNewTicket,
    handleBackToTicketList,

    // Profile
    agentData,

    // Traveller Chat
    travellerChats,
    loadingChats,
    chatSearchTerm,
    setChatSearchTerm,
    chatFilter,
    setChatFilter,
    chatThread,
    activeVoucherInfo,
    travellerMessageText,
    setTravellerMessageText,
    travellerChatAttachment,
    setTravellerChatAttachment,
    travellerChatAttachmentPreview,
    setTravellerChatAttachmentPreview,
    isSendingTravellerMsg,
    handleSendTravellerMessage,
    handleTravellerAttachmentChange,
    totalUnreadTravellerCount,
    getFilteredTravellerChats,
    travellerChatContainerRef,
    travellerFileInputRef,

    // Helpdesk Tickets
    tickets,
    loadingTickets,
    ticketSearchTerm,
    setTicketSearchTerm,
    issue,
    setIssue,
    screenshot,
    setScreenshot,
    screenshotPreview,
    setScreenshotPreview,
    ticketReplies,
    setTicketReplies,
    chatAttachment,
    setChatAttachment,
    chatAttachmentPreview,
    setChatAttachmentPreview,
    isSubmitting,
    isSendingMessage,
    handleSubmitTicket,
    handleSendTicketMessage,
    handleScreenshotChange,
    handleChatAttachmentChange,
    filteredTickets,
    selectedTicket,
    chatEndRef,
    chatFileInputRef
  };

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
};

export default SupportContext;
